import React, { useState, useEffect, useRef } from 'react';
import './QuizResults.css';

const QuizResults = ({ score, totalQuestions, randomQuestions, selectedAnswers, onRetakeQuiz }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const dataArrayRef = useRef(null);
  const audioSetupCompleteRef = useRef(false);

  // Calculate number of correct answers
  const correctAnswers = selectedAnswers.reduce((count, answer, index) => {
    if (answer === undefined || !randomQuestions[index]) return count;
    return answer.answerIndex === randomQuestions[index].correctAnswer ? count + 1 : count;
  }, 0);

  // Calculate points for a single question
  const getQuestionPoints = (answer, question) => {
    if (!answer || answer.answerIndex !== question.correctAnswer) return 0;
    const timeTaken = answer.timeTaken / 1000; // Convert to seconds
    const maxTime = 60;
    const maxScore = 10000;
    const minScore = 5000;
    if (timeTaken >= maxTime) return minScore;
    const decayFactor = Math.pow(timeTaken / maxTime, 0.75);
    return Math.round(minScore + (maxScore - minScore) * (1 - decayFactor));
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    } else {
      setCurrentQuestionIndex(randomQuestions.length - 1);
    }
  };

  const goToNextQuestion = () => {
    if (currentQuestionIndex === null) {
      setCurrentQuestionIndex(0);
    } else if (currentQuestionIndex < randomQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setCurrentQuestionIndex(0);
    }
  };

  const handleBarClick = (index) => {
    setCurrentQuestionIndex(index);
  };

  // Réinitialiser l'audio lorsque la question change
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [currentQuestionIndex]);

  // Nettoyer les ressources audio à la sortie du composant
  useEffect(() => {
    return () => {
      if (sourceRef.current) {
        try {
          sourceRef.current.disconnect();
        } catch (e) {
          console.log('Source déjà déconnectée');
        }
      }
      if (analyserRef.current) {
        try {
          analyserRef.current.disconnect();
        } catch (e) {
          console.log('Analyseur déjà déconnecté');
        }
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        try {
          audioContextRef.current.close();
        } catch (e) {
          console.log('Contexte audio déjà fermé');
        }
      }
    };
  }, []);

  // Afficher l'onde statique au premier rendu si un son est présent
  useEffect(() => {
    if (currentQuestionIndex !== null && randomQuestions[currentQuestionIndex].sound) {
      renderStaticWave();
    }
  }, [currentQuestionIndex, randomQuestions]);

  const renderStaticWave = () => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = 'rgba(27, 94, 32, 0.05)';
    ctx.fillRect(0, 0, width, height);

    ctx.beginPath();
    ctx.moveTo(0, height / 2);
    ctx.lineTo(width, height / 2);
    ctx.strokeStyle = 'rgba(27, 94, 32, 0.2)';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.beginPath();
    let x = 0;
    const sliceWidth = width / 50;
    for (let i = 0; i < 50; i++) {
      const amp = Math.random() * 5 + 2;
      const y = height / 2 + Math.sin(i * 0.2) * amp;
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
      x += sliceWidth;
    }
    ctx.strokeStyle = '#2e7d32';
    ctx.lineWidth = 1.5;
    ctx.stroke();
  };

  const setupAudioVisualizer = () => {
    if (!audioRef.current || audioSetupCompleteRef.current) return;
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext();
      }
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 128;
      const bufferLength = analyserRef.current.frequencyBinCount;
      dataArrayRef.current = new Uint8Array(bufferLength);
      sourceRef.current = audioContextRef.current.createMediaElementSource(audioRef.current);
      sourceRef.current.connect(analyserRef.current);
      analyserRef.current.connect(audioContextRef.current.destination);
      audioSetupCompleteRef.current = true;
      renderStaticWave();
    } catch (error) {
      console.error("Erreur lors de l'initialisation de l'audio:", error);
      renderStaticWave();
    }
  };

  const renderWaveform = () => {
    if (!canvasRef.current || !analyserRef.current || !dataArrayRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    analyserRef.current.getByteFrequencyData(dataArrayRef.current);
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = 'rgba(27, 94, 32, 0.05)';
    ctx.fillRect(0, 0, width, height);

    ctx.beginPath();
    ctx.moveTo(0, height / 2);
    ctx.lineTo(width, height / 2);
    ctx.strokeStyle = 'rgba(27, 94, 32, 0.2)';
    ctx.lineWidth = 1;
    ctx.stroke();

    const barWidth = width / dataArrayRef.current.length;
    for (let i = 0; i < dataArrayRef.current.length; i++) {
      const barHeight = dataArrayRef.current[i] / 255 * height / 2;
      const x = i * barWidth;
      ctx.fillStyle = `rgba(46, 125, 50, ${0.5 + barHeight / height})`;
      ctx.fillRect(x, height / 2 - barHeight, barWidth - 1, barHeight);
      ctx.fillRect(x, height / 2, barWidth - 1, barHeight);
    }

    ctx.beginPath();
    ctx.moveTo(0, height / 2);
    const sliceWidth = width / dataArrayRef.current.length;
    let x = 0;
    for (let i = 0; i < dataArrayRef.current.length; i++) {
      const v = dataArrayRef.current[i] / 255.0;
      const y = height / 2 - v * (height / 4);
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.quadraticCurveTo(x - sliceWidth / 2, y, x, y);
      }
      x += sliceWidth;
    }
    ctx.lineTo(width, height / 2);
    ctx.strokeStyle = '#1b5e20';
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(0, height / 2);
    x = 0;
    for (let i = 0; i < dataArrayRef.current.length; i++) {
      const v = dataArrayRef.current[i] / 255.0;
      const y = height / 2 + v * (height / 4);
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.quadraticCurveTo(x - sliceWidth / 2, y, x, y);
      }
      x += sliceWidth;
    }
    ctx.lineTo(width, height / 2);
    ctx.strokeStyle = '#1b5e20';
    ctx.lineWidth = 2;
    ctx.stroke();

    animationRef.current = requestAnimationFrame(renderWaveform);
  };

  const handlePlay = () => {
    if (!audioRef.current) return;
    try {
      if (!audioSetupCompleteRef.current) {
        setupAudioVisualizer();
      }
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }
      audioRef.current.play().then(() => {
        setIsPlaying(true);
        renderWaveform();
      }).catch((error) => {
        console.error("Erreur lors de la lecture:", error);
        setIsPlaying(false);
        renderStaticWave();
      });
    } catch (error) {
      console.error("Erreur lors de la lecture:", error);
      setIsPlaying(false);
      renderStaticWave();
    }
  };

  const handlePause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      renderStaticWave();
    }
  };

  const scorePercentage = (correctAnswers / totalQuestions) * 100;
  let encouragementMessage = '';
  if (scorePercentage >= 80) {
    encouragementMessage = 'Excellent travail ! Vous maîtrisez bien ce sujet.';
  } else if (scorePercentage >= 60) {
    encouragementMessage = 'Bon travail ! Vous avez une bonne compréhension du sujet.';
  } else if (scorePercentage >= 40) {
    encouragementMessage = 'Continuez à pratiquer pour améliorer vos connaissances.';
  } else {
    encouragementMessage = "N'abandonnez pas ! Avec de la pratique, vous progresserez.";
  }

  const getAltText = (imagePath) => {
    if (imagePath && imagePath.endsWith('.svg') && imagePath.includes('/images/Drapeau/')) {
      return 'Drapeau associé à la question';
    }
    return 'Tableau associé à la question';
  };

  const isFlag = (imagePath) => {
    return imagePath && imagePath.endsWith('.svg') && imagePath.includes('/images/Drapeau/');
  };

  const getImageClass = (imagePath) => {
    return isFlag(imagePath) ? 'flag-image' : 'painting-image';
  };

  const getFrameClass = (imagePath) => {
    return isFlag(imagePath) ? 'flag-container' : 'painting-frame';
  };

  return (
    <div className="quiz-results-container">
      <h2 className="results-title">Résultats du Quiz</h2>

      <div className="score-visual">
        <div className="score-percentage">
          <div
            className="percentage-circle"
            style={{
              background: `conic-gradient(var(--success-color) 0% ${scorePercentage}%, var(--neutral-color) ${scorePercentage}% 100%)`,
            }}
          >
            <span className="percentage-text">{Math.round(scorePercentage)}%</span>
          </div>
        </div>
        <div className="score-text">
          <p className="results-score">
            Votre score : <span className="score-value">{score}</span> points<br />
            <span className="correct-answers">{correctAnswers}</span> /{' '}
            <span className="total-questions">{totalQuestions}</span> réponses correctes
          </p>
          <p className="encouragement-message">{encouragementMessage}</p>
        </div>
      </div>

      <div className="multi-bar-container">
        <h3>Récapitulatif des réponses :</h3>
        <div className="multi-bar">
          {randomQuestions.map((q, index) => {
            const isCorrect = selectedAnswers[index]?.answerIndex === q.correctAnswer;
            return (
              <div
                key={index}
                className={`bar-segment ${isCorrect ? 'correct' : 'incorrect'} ${
                  currentQuestionIndex === index ? 'active' : ''
                }`}
                style={{ width: `${100 / totalQuestions}%` }}
                onClick={() => handleBarClick(index)}
                title={`Question ${index + 1}: ${isCorrect ? 'Correcte' : 'Incorrecte'}`}
              >
                {index + 1}
              </div>
            );
          })}
        </div>
        <div className="bar-legend">
          <div className="legend-item">
            <div className="legend-color correct"></div>
            <span>Réponses correctes</span>
          </div>
          <div className="legend-item">
            <div className="legend-color incorrect"></div>
            <span>Réponses incorrectes</span>
          </div>
        </div>
      </div>

      {currentQuestionIndex !== null && (
        <div className="question-detail-card">
          <div className="navigation-controls">
            <button className="nav-button" onClick={goToPreviousQuestion}>
              ← Précédent
            </button>
            <span className="question-counter">
              Question {currentQuestionIndex + 1} sur {totalQuestions}
            </span>
            <button className="nav-button" onClick={goToNextQuestion}>
              Suivant →
            </button>
          </div>

          {selectedAnswers[currentQuestionIndex] && (
            <div className="question-stats">
              <span>Temps: {(selectedAnswers[currentQuestionIndex].timeTaken / 1000).toFixed(2)} secondes</span>
              <span className={`points ${selectedAnswers[currentQuestionIndex].answerIndex === randomQuestions[currentQuestionIndex].correctAnswer ? 'correct' : 'incorrect'}`}>
                {`+${getQuestionPoints(selectedAnswers[currentQuestionIndex], randomQuestions[currentQuestionIndex])} points`}
              </span>
            </div>
          )}

          <div className="question-content">
            {randomQuestions[currentQuestionIndex].image && (
              <div className={getFrameClass(randomQuestions[currentQuestionIndex].image)}>
                <img
                  src={randomQuestions[currentQuestionIndex].image}
                  alt={getAltText(randomQuestions[currentQuestionIndex].image)}
                  className={getImageClass(randomQuestions[currentQuestionIndex].image)}
                />
              </div>
            )}
            {randomQuestions[currentQuestionIndex].sound && (
              <div className={`sound-player ${isPlaying ? 'playing' : ''}`}>
                <audio
                  ref={audioRef}
                  src={randomQuestions[currentQuestionIndex].sound}
                  loop
                  preload="auto"
                  onEnded={() => setIsPlaying(false)}
                  onCanPlay={() => {
                    if (!audioSetupCompleteRef.current) {
                      setupAudioVisualizer();
                    }
                  }}
                >
                  Votre navigateur ne supporte pas l’élément audio.
                </audio>
                <div className="audio-visualizer">
                  <canvas
                    ref={canvasRef}
                    width="300"
                    height="80"
                    className="visualizer-canvas"
                  />
                </div>
                <div className="audio-controls">
                  <button
                    onClick={handlePlay}
                    disabled={isPlaying}
                    className="play-btn"
                  >
                    <span className="btn-icon">▶</span> Écouter
                  </button>
                  <button
                    onClick={handlePause}
                    disabled={!isPlaying}
                    className="pause-btn"
                  >
                    <span className="btn-icon">❚❚</span> Pause
                  </button>
                </div>
              </div>
            )}
            <h3 className="question-text">{randomQuestions[currentQuestionIndex].question}</h3>

            <div className="answers-container">
              {randomQuestions[currentQuestionIndex].answers.map((answer, answerIndex) => (
                <div
                  key={answerIndex}
                  className={`answer-option ${
                    answerIndex === randomQuestions[currentQuestionIndex].correctAnswer
                      ? 'correct-answer'
                      : answerIndex === selectedAnswers[currentQuestionIndex]?.answerIndex
                      ? 'user-answer incorrect'
                      : ''
                  }`}
                >
                  <div className="answer-marker">
                    {answerIndex === randomQuestions[currentQuestionIndex].correctAnswer
                      ? '✓'
                      : answerIndex === selectedAnswers[currentQuestionIndex]?.answerIndex &&
                        answerIndex !== randomQuestions[currentQuestionIndex].correctAnswer
                      ? '✗'
                      : ''
                    }
                  </div>
                  <div className="answer-text">{answer}</div>
                </div>
              ))}
            </div>

            {selectedAnswers[currentQuestionIndex]?.answerIndex !==
              randomQuestions[currentQuestionIndex].correctAnswer && (
              <p className="feedback">
                Conseil : Révisez cette question pour améliorer votre compréhension.
              </p>
            )}
          </div>
        </div>
      )}

      {currentQuestionIndex === null && (
        <div className="instruction-message">
          <p>
            Cliquez sur un segment de la barre ou sur le bouton ci-dessous pour examiner vos
            réponses
          </p>
          <button className="review-button" onClick={goToNextQuestion}>
            Examiner les questions
          </button>
        </div>
      )}

      <button className="retake-quiz-button" onClick={onRetakeQuiz}>
        Relancer le Quiz
      </button>
    </div>
  );
};

export default QuizResults;